import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Loader2 } from "lucide-react";
import { Eyebrow } from "@/components/landing/SiteLayout";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTurnstile } from "@/components/TurnstileProvider";
import { turnstileEnabled } from "@/lib/turnstile";
import { z } from "zod";

const schema = z.object({
  name: z.string().trim().min(1, "Bitte Namen angeben").max(200),
  email: z.string().trim().email("Ungültige E-Mail").max(255),
  telefon: z.string().trim().max(50).optional(),
  betreff: z.string().trim().max(200).optional(),
  nachricht: z.string().trim().min(1, "Bitte Nachricht angeben").max(3000),
  einverstanden: z.literal(true, { errorMap: () => ({ message: "Bitte AGB und Datenschutz akzeptieren" }) }),
});

export function KontaktSection() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", telefon: "", betreff: "", nachricht: "" });
  const [einverstanden, setEinverstanden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { getToken, reset: resetTurnstile } = useTurnstile();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ ...form, einverstanden });
    if (!parsed.success) {
      toast({ title: "Bitte prüfen Sie Ihre Angaben", description: parsed.error.issues[0]?.message, variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      let turnstileToken: string | null = null;
      if (turnstileEnabled()) {
        turnstileToken = await getToken();
        if (!turnstileToken) {
          toast({
            title: "Sicherheitsprüfung fehlgeschlagen",
            description: "Bitte laden Sie die Seite neu und versuchen Sie es erneut.",
            variant: "destructive",
          });
          return;
        }
      }

      const { data, error } = await supabase.functions.invoke("submit-kontakt", {
        body: { ...parsed.data, turnstileToken },
      });
      if (error) {
        resetTurnstile();
        const msg = (data as { error?: string } | null)?.error || "Senden fehlgeschlagen. Bitte später erneut versuchen.";
        toast({ title: "Fehler", description: msg, variant: "destructive" });
        return;
      }
      resetTurnstile();
      setSent(true);
      toast({ title: "Nachricht gesendet", description: "Wir melden uns zeitnah zurück." });
      setForm({ name: "", email: "", telefon: "", betreff: "", nachricht: "" });
      setEinverstanden(false);
    } finally {
      setLoading(false);
    }
  };


  return (
    <section id="kontakt" className="py-14 sm:py-20 bg-muted/40 scroll-mt-16">
      <div className="max-w-2xl px-6 mx-auto">
        <Eyebrow>Jetzt starten</Eyebrow>
        <h2 className="mt-6 text-3xl sm:text-[44px] font-semibold tracking-[-0.03em] text-foreground leading-[1.1]">
          Liftanfrage kostenlos erfassen.
        </h2>
        <p className="mt-5 text-[15.5px] text-muted-foreground leading-[1.7]">
          Beschreiben Sie Ihr Liftprojekt online. Passende Liftanbieter melden sich direkt bei Ihnen.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
          <Button
            size="lg"
            onClick={() => navigate("/wizard")}
            className="min-h-[52px] px-8 rounded-sm text-[15.5px] font-semibold w-full sm:w-auto transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
          >
            Liftanfrage kostenlos erfassen
          </Button>
          <Link
            to="/#so-funktioniert"
            className="inline-flex h-12 items-center justify-center px-1 text-[15px] font-medium text-foreground border-b border-border hover:border-foreground transition-colors"
          >
            So funktioniert es
          </Link>
        </div>

        <div className="mt-12 rounded-3xl border border-border/70 bg-background p-6 sm:p-9 shadow-[0_1px_2px_rgba(15,23,42,0.03),0_18px_44px_-32px_rgba(15,23,42,0.25)]">
          <h3 className="text-[22px] sm:text-[27px] font-semibold tracking-[-0.02em] text-foreground leading-[1.2]">
            Sie haben eine Frage? Schreiben Sie uns.
          </h3>
          <p className="mt-3 text-[14.5px] text-muted-foreground leading-[1.7]">
            Wir antworten in der Regel innerhalb eines Werktages - persönlich und ohne Verkaufsdruck. Oder direkt per E-Mail an{" "}
            <a href="mailto:info@liftblick.ch" className="font-medium text-foreground hover:underline underline-offset-4">
              info@liftblick.ch
            </a>
            .
          </p>

          {sent ? (
            <div className="mt-7 rounded-2xl bg-muted/60 p-6">
              <p className="text-[16px] font-semibold text-foreground">Danke für Ihre Nachricht.</p>
              <p className="mt-2 text-[14.5px] text-muted-foreground leading-[1.7]">
                Wir haben Ihre Anfrage erhalten und melden uns in der Regel innerhalb eines Werktages per E-Mail zurück.
              </p>
              <a
                href="mailto:info@liftblick.ch"
                className="mt-4 inline-flex items-center gap-2 text-[14px] font-semibold text-foreground hover:underline underline-offset-4"
              >
                <Mail className="w-4 h-4" />
                info@liftblick.ch
              </a>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-8 grid gap-5" noValidate>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="grid gap-2">
                  <Label htmlFor="k-name" className="text-[13.5px] font-medium">Name</Label>
                  <Input id="k-name" required placeholder="Vor- und Nachname" className="h-12 rounded-sm bg-muted/30 text-[15px]" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={200} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="k-email" className="text-[13.5px] font-medium">E-Mail</Label>
                  <Input id="k-email" type="email" required placeholder="name@beispiel.ch" className="h-12 rounded-sm bg-muted/30 text-[15px]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} maxLength={255} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="grid gap-2">
                  <Label htmlFor="k-telefon" className="text-[13.5px] font-medium">
                    Telefon <span className="font-normal text-muted-foreground">(optional)</span>
                  </Label>
                  <Input id="k-telefon" placeholder="079 123 45 67" className="h-12 rounded-sm bg-muted/30 text-[15px]" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} maxLength={50} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="k-betreff" className="text-[13.5px] font-medium">
                    Betreff <span className="font-normal text-muted-foreground">(optional)</span>
                  </Label>
                  <Input id="k-betreff" placeholder="Worum geht es?" className="h-12 rounded-sm bg-muted/30 text-[15px]" value={form.betreff} onChange={(e) => setForm({ ...form, betreff: e.target.value })} maxLength={200} />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="k-nachricht" className="text-[13.5px] font-medium">Nachricht</Label>
                <Textarea id="k-nachricht" required rows={5} placeholder="Beschreiben Sie kurz Ihr Anliegen." className="rounded-sm bg-muted/30 text-[15px] leading-[1.7]" value={form.nachricht} onChange={(e) => setForm({ ...form, nachricht: e.target.value })} maxLength={3000} />
              </div>
              <label className="flex items-start gap-3 text-[13.5px] text-muted-foreground leading-[1.6]">
                <Checkbox checked={einverstanden} onCheckedChange={(v) => setEinverstanden(v === true)} className="mt-0.5" aria-label="AGB und Datenschutz akzeptieren" />
                <span>
                  Ich akzeptiere die <Link to="/agb" className="underline underline-offset-2 text-foreground">AGB</Link> und die{" "}
                  <Link to="/datenschutz" className="underline underline-offset-2 text-foreground">Datenschutzerklärung</Link>.
                </span>
              </label>
              <div className="mt-1">
                <Button
                  type="submit"
                  size="lg"
                  disabled={loading}
                  className="w-full sm:w-auto min-h-[52px] px-8 rounded-sm text-[15.5px] font-semibold transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Senden…</> : <>Nachricht senden</>}
                </Button>
                <p className="mt-3 text-[12.5px] text-muted-foreground leading-[1.6]">
                  Antwort in der Regel innerhalb eines Werktages.
                  {turnstileEnabled() && " Spamschutz durch Cloudflare Turnstile."}
                </p>
              </div>
            </form>
          )}
        </div>
      </div>

    </section>
  );
}
